import 'react-quill-new/dist/quill.snow.css';

import React, { FC, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import ReactQuill, { Quill } from 'react-quill-new';
import { normalizeNonBreakingSpaces } from '../Utils/html/normalizeNonBreakingSpaces';

export { Quill };

// Register custom BlankTargetLink format once
let isBlankTargetLinkRegistered = false;

function registerBlankTargetLink() {
  if (isBlankTargetLinkRegistered) return;

  const Link = Quill.import('formats/link') as any;
  const originalCreate = Link.create.bind(Link);

  Link.create = function (value: string) {
    const node = originalCreate(value);
    node.setAttribute('target', '_blank');
    return node;
  };

  isBlankTargetLinkRegistered = true;
}

// Normalise Quill's whitespace serialisation once (Bug #11522)
let isSpaceNormalizationRegistered = false;

/**
 * `react-quill-new` reads the editor value through `Quill.getSemanticHTML()`,
 * and Quill 2 serialises *every* regular space in there as `&nbsp;` to keep the
 * whitespace byte-exact. A text made of non-breaking spaces is one unbreakable
 * token, so it overflows its container, is chopped mid-word, or breaks at the
 * wrong positions in a sent email.
 *
 * Patching the serialiser (instead of the `onChange` value) keeps the value the
 * consumer receives and the value `react-quill-new` compares against in sync,
 * so a controlled editor is not rebuilt on every keystroke and the caret stays
 * where the user put it.
 */
function registerSpaceNormalization() {
  if (isSpaceNormalizationRegistered) return;

  const quillPrototype = Quill.prototype as unknown as {
    getSemanticHTML: (index?: number, length?: number) => string;
  };
  const originalGetSemanticHTML = quillPrototype.getSemanticHTML;

  quillPrototype.getSemanticHTML = function (index?: number, length?: number) {
    return normalizeNonBreakingSpaces(originalGetSemanticHTML.call(this, index, length));
  };

  isSpaceNormalizationRegistered = true;
}

export interface AixQuillProps extends React.ComponentProps<typeof ReactQuill> {
  maxLength?: number;
}

export const AixQuill: FC<AixQuillProps> = props => {
  // Register custom link format and whitespace normalisation on first render
  registerBlankTargetLink();
  registerSpaceNormalization();

  const quillReference = useRef<ReactQuill>(null);
  const { t } = useTranslation();

  // Add aria-labels to Quill toolbar buttons
  useEffect(() => {
    if (!quillReference.current) {
      return;
    }

    const quill = quillReference.current.getEditor();
    const toolbar = (quill.getModule('toolbar') as any)?.container;

    if (!toolbar) {
      return;
    }

    const addQuillAriaLabels = () => {
      const buttons = [
        { selector: '.ql-bold', label: t('quill_bold', { defaultValue: 'Fett' }) },
        { selector: '.ql-italic', label: t('quill_italic', { defaultValue: 'Kursiv' }) },
        {
          selector: '.ql-underline',
          label: t('quill_underline', { defaultValue: 'Unterstrichen' }),
        },
        {
          selector: '.ql-strike',
          label: t('quill_strike', { defaultValue: 'Durchgestrichen' }),
        },
        {
          selector: '.ql-header[value="1"]',
          label: t('quill_header1', { defaultValue: 'Überschrift 1' }),
        },
        {
          selector: '.ql-header[value="2"]',
          label: t('quill_header2', { defaultValue: 'Überschrift 2' }),
        },
        {
          selector: '.ql-list[value="ordered"]',
          label: t('quill_ordered_list', { defaultValue: 'Nummerierte Liste' }),
        },
        {
          selector: '.ql-list[value="bullet"]',
          label: t('quill_bullet_list', { defaultValue: 'Aufzählungsliste' }),
        },
        {
          selector: '.ql-script[value="sub"]',
          label: t('quill_subscript', { defaultValue: 'Tiefgestellt' }),
        },
        {
          selector: '.ql-script[value="super"]',
          label: t('quill_superscript', { defaultValue: 'Hochgestellt' }),
        },
        {
          selector: '.ql-indent[value="-1"]',
          label: t('quill_outdent', { defaultValue: 'Einzug verringern' }),
        },
        {
          selector: '.ql-indent[value="+1"]',
          label: t('quill_indent', { defaultValue: 'Einzug erhöhen' }),
        },
        {
          selector: '.ql-direction[value="rtl"]',
          label: t('quill_rtl', { defaultValue: 'Textrichtung rechts-nach-links' }),
        },
        { selector: '.ql-color', label: t('quill_color', { defaultValue: 'Textfarbe' }) },
        {
          selector: '.ql-background',
          label: t('quill_background', { defaultValue: 'Hintergrundfarbe' }),
        },
        { selector: '.ql-font', label: t('quill_font', { defaultValue: 'Schriftart' }) },
        { selector: '.ql-align', label: t('quill_align', { defaultValue: 'Ausrichtung' }) },
        {
          selector: '.ql-clean',
          label: t('quill_clean', { defaultValue: 'Formatierung entfernen' }),
        },
        { selector: '.ql-image', label: t('quill_image', { defaultValue: 'Bild einfügen' }) },
      ];

      buttons.forEach(({ selector, label }) => {
        const button = toolbar.querySelector(selector);
        if (button && !button.getAttribute('aria-label')) {
          button.setAttribute('aria-label', label);
        }
      });

      // Add aria-labels to picker labels (the .ql-picker-label elements)
      const colorPickerLabel = toolbar.querySelector(
        '.ql-color .ql-picker-label',
      );
      if (colorPickerLabel && !colorPickerLabel.getAttribute('aria-label')) {
        colorPickerLabel.setAttribute(
          'aria-label',
          t('quill_color', { defaultValue: 'Textfarbe' }),
        );
      }

      const backgroundPickerLabel = toolbar.querySelector(
        '.ql-background .ql-picker-label',
      );
      if (
        backgroundPickerLabel &&
        !backgroundPickerLabel.getAttribute('aria-label')
      ) {
        backgroundPickerLabel.setAttribute(
          'aria-label',
          t('quill_background', { defaultValue: 'Hintergrundfarbe' }),
        );
      }

      const fontPickerLabel = toolbar.querySelector(
        '.ql-font .ql-picker-label',
      );
      if (fontPickerLabel && !fontPickerLabel.getAttribute('aria-label')) {
        fontPickerLabel.setAttribute(
          'aria-label',
          t('quill_font', { defaultValue: 'Schriftart' }),
        );
      }

      const alignPickerLabel = toolbar.querySelector(
        '.ql-align .ql-picker-label',
      );
      if (alignPickerLabel && !alignPickerLabel.getAttribute('aria-label')) {
        alignPickerLabel.setAttribute(
          'aria-label',
          t('quill_align', { defaultValue: 'Ausrichtung' }),
        );
      }
    };

    addQuillAriaLabels();
  }, [t]);

  useEffect(() => {
    if (!props.maxLength || props.maxLength <= 0) {
      return;
    }

    if (!quillReference || !quillReference.current) {
      return;
    }

    // Based on https://stackoverflow.com/a/78107163
    const quill = quillReference.current.getEditor();
    quill.on('text-change', () => {
      const textLength = quill.getLength() - 1; // -1 to exclude the last newline character
      if (props.maxLength && textLength > props.maxLength) {
        quill.deleteText(props.maxLength, textLength - props.maxLength);
      }
    });
  }, [props.maxLength]);

  const className = props.readOnly
    ? 'aixQuillReadonly'
    : props.className ?? 'aixQuill';

  return <ReactQuill {...props} className={className} ref={quillReference} />;
};
